import classNames from 'classnames';
import React, { useContext } from 'react';
import { ThinArrowIcon } from 'src/components/shared/elements/icons/ThinArrowIcon';
import { ANIMATION_CLASSES, getLargeCategoryIcon } from 'src/components/shared/templates/header/HamburgerMenu/index';
import type { LargeCategorySchema, MiddleCategorySchema } from 'src/models/category/schema';
import { CategoryContext } from 'src/stores/Category';
import { I18nContext } from 'src/stores/I18n';
import { mixpanel } from 'src/utils/mixpanel';

export const CategoryListForSp = ({
  breadcrumbs,
  currentLargeCategory,
  currentMiddleCategory,
  animationClass,
  setCurrentLargeCategory,
  setCurrentMiddleCategory,
  setBreadcrumbs,
  handleSlideDirection
}: {
  breadcrumbs: string[];
  currentLargeCategory: number | null;
  currentMiddleCategory: number | null;
  animationClass: 'animate-slideToLeft' | 'animate-slideToRight' | 'unset';
  setCurrentLargeCategory: (id: number | null) => void;
  setCurrentMiddleCategory: (id: number | null) => void;
  setBreadcrumbs: (breadcrumbs: string[] | ((prev: string[]) => string[])) => void;
  handleSlideDirection: (direction: 'animate-slideToLeft' | 'animate-slideToRight') => void;
}) => {
  const categories = useContext(CategoryContext);
  const localeResources = useContext(I18nContext);
  const breadcrumbsLength = breadcrumbs.length;

  const handleBreadcrumbClick = (index: number) => {
    if (index === 0) {
      setBreadcrumbs([localeResources.category.title]);
      setCurrentLargeCategory(null);
      setCurrentMiddleCategory(null);
      handleSlideDirection(ANIMATION_CLASSES.SLIDE_TO_RIGHT);
    } else if (index === 1) {
      setBreadcrumbs(breadcrumbs.slice(0, 2));
      const largeCategory = categories.largeCategories.find((large) => large.name === breadcrumbs[1]);
      setCurrentLargeCategory(largeCategory?.largeCategoryId || null);
      setCurrentMiddleCategory(null);
      handleSlideDirection(ANIMATION_CLASSES.SLIDE_TO_RIGHT);
    } else if (index === 2) {
      setBreadcrumbs(breadcrumbs.slice(0, 3));
      const middleCategory = categories.middleCategories.find((middle) => middle.name === breadcrumbs[2]);
      setCurrentMiddleCategory(middleCategory?.middleCategoryId || null);
    }
  };

  const handleLargeCategoryClick = (largeCategory: LargeCategorySchema) => {
    setBreadcrumbs([localeResources.category.title, largeCategory.name]);
    setCurrentLargeCategory(largeCategory.largeCategoryId);
    handleSlideDirection(ANIMATION_CLASSES.SLIDE_TO_LEFT);
  };

  const handleMiddleCategoryClick = (middleCategory: MiddleCategorySchema) => {
    setBreadcrumbs((prev) => [...prev, middleCategory.name]);
    setCurrentMiddleCategory(middleCategory.middleCategoryId);
    handleSlideDirection(ANIMATION_CLASSES.SLIDE_TO_LEFT);
  };

  if (!categories) return null;

  return (
    <div className={classNames('pl-5 pr-7 flex flex-col gap-4', animationClass)}>
      <div className="text-base text-gray600 overflow-x-scroll flex flex-nowrap gap-2 w-full hidden-scrollbar">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb}>
            <button
              onClick={() => handleBreadcrumbClick(index)}
              className={classNames(
                'relative text-gray600 whitespace-nowrap inline-block',
                "after:content-[''] after:inline-block after:w-1.5 after:h-2.5 after:ml-2 after:bg-[url('/kaimono/images/common/navigator/arrow.webp')] after:bg-no-repeat after:bg-cover after:bg-center;",
                index === breadcrumbs.length - 1 && 'after:hidden'
              )}
              disabled={index === breadcrumbs.length - 1}
            >
              {crumb}
            </button>
          </React.Fragment>
        ))}
      </div>
      {breadcrumbsLength === 0 && (
        <div
          className="flex items-center cursor-pointer"
          onClick={() => {
            setBreadcrumbs([localeResources.category.title]);
            setCurrentLargeCategory(null);
            setCurrentMiddleCategory(null);
            handleSlideDirection(ANIMATION_CLASSES.SLIDE_TO_LEFT);
          }}
        >
          <span>{localeResources.category.title}</span>
          <ThinArrowIcon className="w-4 h-4 ml-auto" direction="right" />
        </div>
      )}

      {breadcrumbsLength === 1 && !currentLargeCategory && (
        <ul className="flex flex-col gap-8">
          {categories.largeCategories.map((largeCategory) => (
            <li
              key={largeCategory.largeCategoryId}
              className="flex gap-4 items-center cursor-pointer"
              onClick={() => handleLargeCategoryClick(largeCategory)}
            >
              <span className="w-10 grid place-content-center">
                {getLargeCategoryIcon(
                  largeCategory.slug,
                  largeCategory.slug === 'living_travel' ? 'w-10 h-10' : 'w-8 h-8'
                )}
              </span>
              <span className="flex-1 overflow-scroll whitespace-pre scrollbar-none">{largeCategory.name}</span>
              <ThinArrowIcon className="w-4 h-4 ml-auto" direction="right" />
            </li>
          ))}
        </ul>
      )}

      {breadcrumbsLength === 2 && currentLargeCategory && !currentMiddleCategory && (
        <>
          <div className="py-2 flex items-center gap-4">
            <span className="grid place-content-center">
              {getLargeCategoryIcon(
                categories.largeCategories.find((large) => large.largeCategoryId === currentLargeCategory)?.slug || '',
                'w-8 h-8'
              )}
            </span>
            <a
              className="overflow-scroll whitespace-pre scrollbar-none"
              href={`/kaimono/large_categories/${currentLargeCategory}`}
              rel="noopener noreferrer"
              onClick={() => {
                mixpanel.onHamburgerCategoryClicked('large', currentLargeCategory);
              }}
            >
              {categories.largeCategories.find((large) => large.largeCategoryId === currentLargeCategory)?.name}
            </a>
          </div>
          <ul className="flex flex-col gap-4 pl-7">
            {categories.middleCategories
              .filter((middle) =>
                categories.largeCategories
                  .find((large) => large.largeCategoryId === currentLargeCategory)
                  ?.middleCategoryIds.includes(middle.middleCategoryId)
              )
              .map((middleCategory) => (
                <li
                  key={middleCategory.middleCategoryId}
                  className="py-2 flex items-center cursor-pointer"
                  onClick={() => handleMiddleCategoryClick(middleCategory)}
                >
                  <span className="flex-1">{middleCategory.name}</span>
                  <ThinArrowIcon className="w-4 h-4 ml-auto" direction="right" />
                </li>
              ))}
          </ul>
        </>
      )}

      {breadcrumbsLength === 3 && currentMiddleCategory && (
        <>
          <a
            href={`/kaimono/middle_categories/${currentMiddleCategory}`}
            rel="noopener noreferrer"
            className="py-2"
            onClick={() => {
              mixpanel.onHamburgerCategoryClicked('middle', currentMiddleCategory);
            }}
          >
            {categories.middleCategories.find((middle) => middle.middleCategoryId === currentMiddleCategory)?.name}
          </a>
          <ul className="flex flex-col gap-4 px-7">
            {categories.smallCategories
              .filter((small) =>
                categories.middleCategories
                  .find((middle) => middle.middleCategoryId === currentMiddleCategory)
                  ?.smallCategoryIds.includes(small.smallCategoryId)
              )
              .map((smallCategory) => (
                <li key={smallCategory.smallCategoryId} className="py-2 flex items-center cursor-pointer">
                  <a
                    href={`/kaimono/small_categories/${smallCategory.smallCategoryId}`}
                    rel="noopener noreferrer"
                    onClick={() => {
                      mixpanel.onHamburgerCategoryClicked('small', smallCategory.smallCategoryId);
                    }}
                  >
                    {smallCategory.name}
                  </a>
                </li>
              ))}
          </ul>
        </>
      )}
    </div>
  );
};
